import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { usePushSubscription } from "@/lib/push";
import {
  getNotificationPreferences,
  upsertNotificationPreferences,
  sendTestNotification,
} from "@/lib/push.functions";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bell, BellOff, Send } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/notifications")({
  component: NotificationSettings,
  head: () => ({ meta: [{ title: "Notification settings — MissionReady" }] }),
});

const CATEGORIES: { key: string; label: string; desc: string }[] = [
  { key: "shift_reminders", label: "Shift reminders", desc: "Reminders before your scheduled shifts." },
  { key: "credential_expiry", label: "Credential expiry", desc: "Heads-up when a credential is about to expire." },
  { key: "compliance", label: "Compliance alerts", desc: "Block/warn when a shift fails compliance." },
  { key: "dispatch", label: "Dispatch", desc: "Assignments and broadcasts from dispatch." },
  { key: "incident", label: "Incidents", desc: "New, assigned, or status-changed incidents." },
  { key: "sos", label: "SOS / Panic", desc: "Officer SOS triggers." },
  { key: "message", label: "Messages", desc: "Channel and direct message activity." },
];

type Prefs = {
  push_enabled: boolean;
  in_app_enabled: boolean;
  categories: Record<string, boolean>;
};

function NotificationSettings() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? null;
  const push = usePushSubscription(orgId);
  const fetchPrefs = useServerFn(getNotificationPreferences);
  const savePrefs = useServerFn(upsertNotificationPreferences);
  const testFn = useServerFn(sendTestNotification);
  const [prefs, setPrefs] = useState<Prefs>({
    push_enabled: true,
    in_app_enabled: true,
    categories: Object.fromEntries(CATEGORIES.map((c) => [c.key, true])),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    fetchPrefs({ data: { organizationId: orgId } }).then(({ preferences }) => {
      if (preferences) {
        setPrefs({
          push_enabled: preferences.push_enabled,
          in_app_enabled: preferences.in_app_enabled,
          categories: {
            ...Object.fromEntries(CATEGORIES.map((c) => [c.key, true])),
            ...((preferences.categories as Record<string, boolean>) ?? {}),
          },
        });
      }
    });
  }, [orgId, fetchPrefs]);

  const persist = async (next: Prefs) => {
    if (!orgId) return;
    setPrefs(next);
    setSaving(true);
    try {
      await savePrefs({ data: { organizationId: orgId, ...next } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save preferences");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!orgId) return;
    try {
      const r = await testFn({ data: { organizationId: orgId } });
      if (r.attempts === 0) toast.warning("No active push subscriptions on this account.");
      else if (r.failures === r.attempts) toast.error("All push deliveries failed.");
      else toast.success(`Test push sent (${r.attempts - r.failures}/${r.attempts}).`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Test failed");
    }
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8 max-w-3xl">
      <h1 className="font-display text-2xl font-bold md:text-3xl">Notifications</h1>
      <p className="text-sm text-muted-foreground">Control how MissionReady reaches you.</p>

      {/* Push enrollment */}
      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <Bell className="mt-1 h-5 w-5 text-primary" />
          <div className="flex-1">
            <h2 className="font-display text-lg font-semibold">Push notifications</h2>
            <p className="text-sm text-muted-foreground">
              Get real-time alerts on this device, even when MissionReady is closed.
            </p>
            <div className="mt-3 text-sm">
              {push.state.status === "unsupported" && <span className="text-muted-foreground">This browser does not support push notifications.</span>}
              {push.state.status === "denied" && <span className="text-destructive">Notifications are blocked in your browser settings.</span>}
              {push.state.status === "not_configured" && <span className="text-muted-foreground">Push is not configured on the server yet.</span>}
              {push.state.status === "loading" && <span className="text-muted-foreground">Checking…</span>}
              {push.state.status === "subscribed" && <span className="text-primary">Enabled on this device.</span>}
              {push.state.status === "unsubscribed" && <span className="text-muted-foreground">Not enabled on this device.</span>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {push.state.status === "subscribed" ? (
              <Button variant="outline" onClick={push.disable}><BellOff className="mr-2 h-4 w-4" /> Disable</Button>
            ) : (
              <Button
                disabled={!["unsubscribed"].includes(push.state.status)}
                onClick={push.enable}
              ><Bell className="mr-2 h-4 w-4" /> Enable</Button>
            )}
            <Button variant="ghost" size="sm" onClick={sendTest}><Send className="mr-2 h-4 w-4" /> Test</Button>
          </div>
        </div>
      </div>

      {/* Channels */}
      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Channels</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
            <div><Label className="text-sm font-medium">In-app</Label><p className="mt-0.5 text-xs text-muted-foreground">Bell menu and notifications page.</p></div>
            <Switch checked={prefs.in_app_enabled} disabled={saving}
              onCheckedChange={(v) => persist({ ...prefs, in_app_enabled: v })} />
          </div>
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
            <div><Label className="text-sm font-medium">Push</Label><p className="mt-0.5 text-xs text-muted-foreground">Browser/device push.</p></div>
            <Switch checked={prefs.push_enabled} disabled={saving}
              onCheckedChange={(v) => persist({ ...prefs, push_enabled: v })} />
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Categories</h2>
        <p className="text-sm text-muted-foreground">Pick which kinds of alerts you receive.</p>
        <div className="mt-3 grid gap-2">
          {CATEGORIES.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
              <div className="min-w-0">
                <Label className="text-sm font-medium">{c.label}</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">{c.desc}</p>
              </div>
              <Switch
                checked={prefs.categories[c.key] !== false}
                disabled={saving}
                onCheckedChange={(v) => persist({ ...prefs, categories: { ...prefs.categories, [c.key]: v } })}
              />
            </div>
          ))}
        </div>
      </div>

      <RoleConversationDefaults />
    </div>
  );
}

// =============================================================
// Per-role conversation mute defaults (org owner/admin only)
// =============================================================
import { supabase } from "@/integrations/supabase/client";

const CHANNEL_KINDS = [
  { key: "dm", label: "Direct messages" },
  { key: "group", label: "Group chats" },
  { key: "site", label: "Site channels" },
  { key: "broadcast", label: "Broadcasts" },
  { key: "team", label: "Team channels" },
];
const ROLES = [
  { key: "officer", label: "Officer" },
  { key: "supervisor", label: "Supervisor" },
  { key: "site_supervisor", label: "Site supervisor" },
  { key: "dispatcher", label: "Dispatcher" },
  { key: "admin", label: "Admin" },
  { key: "auditor", label: "Auditor" },
  { key: "client_viewer", label: "Client viewer" },
];

function RoleConversationDefaults() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const orgId = currentOrg?.id ?? null;
  const canEdit = isPlatformAdmin || currentRole === "org_owner" || currentRole === "admin";
  const [map, setMap] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orgId || !canEdit) return;
    supabase
      .from("role_notification_prefs")
      .select("role, channel_kind, muted")
      .eq("organization_id", orgId)
      .then(({ data }) => {
        const next: Record<string, boolean> = {};
        (data ?? []).forEach((r) => {
          next[`${r.role}::${r.channel_kind}`] = !!r.muted;
        });
        setMap(next);
      });
  }, [orgId, canEdit]);

  if (!canEdit) return null;

  const setMuted = async (role: string, kind: string, muted: boolean) => {
    if (!orgId) return;
    const k = `${role}::${kind}`;
    setMap((p) => ({ ...p, [k]: muted }));
    setBusy(true);
    const { error } = await supabase
      .from("role_notification_prefs")
      .upsert({
        organization_id: orgId,
        role: role as never,
        channel_kind: kind,
        muted,
        updated_at: new Date().toISOString(),
      }, { onConflict: "organization_id,role,channel_kind" });
    if (error) {
      setMap((p) => ({ ...p, [k]: !muted }));
      toast.error("Failed to save default");
    }
    setBusy(false);
  };

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-5">
      <h2 className="font-display text-lg font-semibold">Conversation defaults by role</h2>
      <p className="text-sm text-muted-foreground">
        Mute notifications by default for entire roles. Individual users can still
        unmute or override per conversation. Badges remain accurate either way.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2 pr-3">Role</th>
              {CHANNEL_KINDS.map((c) => (
                <th key={c.key} className="px-2 py-2 text-center">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROLES.map((r) => (
              <tr key={r.key} className="border-t border-border/60">
                <td className="py-2 pr-3 font-medium">{r.label}</td>
                {CHANNEL_KINDS.map((c) => {
                  const k = `${r.key}::${c.key}`;
                  return (
                    <td key={c.key} className="px-2 py-2 text-center">
                      <Switch
                        checked={!!map[k]}
                        disabled={busy}
                        onCheckedChange={(v) => setMuted(r.key, c.key, v)}
                        aria-label={`Mute ${c.label} for ${r.label}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-muted-foreground">
          Toggle on = muted by default for that role + conversation type.
        </p>
      </div>
    </div>
  );
}

